import { DOCUMENT } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { DestroyRef, Injectable, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NavigationEnd, Route, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { filter } from 'rxjs'
import { afterIdle } from '../../shared/utils/after-idle'
import { loadFeatureTranslations } from '../resolvers/translation.resolver'

@Injectable({ providedIn: 'root' })
export class TranslationPrefetchService {
  private document = inject(DOCUMENT)
  private router = inject(Router)
  private http = inject(HttpClient)
  private translate = inject(TranslateService)
  private destroyRef = inject(DestroyRef)
  private started = false

  start(): void {
    if (this.started) return
    this.started = true
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return
    const intent = (event: Event) => {
      const link = (event.target as Element)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (link && link.origin === this.document.location.origin) this.prefetch(link.pathname)
    }
    this.document.addEventListener('pointerover', intent, { passive: true })
    this.document.addEventListener('focusin', intent)
    let cancelIdle: (() => void) | undefined
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        cancelIdle?.()
        cancelIdle = afterIdle(() => {
          const auth = /^\/(login|register|forgot-password|reset-password)(\/|$)/.test(event.urlAfterRedirects)
          for (const path of auth ? ['/login', '/register'] : ['/home', '/products', '/themes']) this.prefetch(path)
        })
      })
    this.destroyRef.onDestroy(() => {
      cancelIdle?.()
      this.document.removeEventListener('pointerover', intent)
      this.document.removeEventListener('focusin', intent)
    })
  }

  private prefetch(path: string): void {
    const files = this.filesFor(this.router.config, path.split('/').filter(Boolean))
    const lang = this.translate.currentLang || this.translate.defaultLang
    if (lang && files.length) loadFeatureTranslations(this.http, this.translate, lang, files).subscribe()
  }

  private filesFor(routes: Route[], segments: string[]): string[] {
    for (const route of routes) {
      if (route.redirectTo !== undefined || route.path === '**') continue
      const parts = (route.path || '').split('/').filter(Boolean)
      if (!parts.every((part, i) => (part.startsWith(':') ? !!segments[i] : part === segments[i]))) continue
      const remaining = segments.slice(parts.length)
      if (remaining.length && !route.children) continue
      const children = remaining.length ? this.filesFor(route.children || [], remaining) : []
      if (remaining.length && !children.length) continue
      return [...(route.data?.['translationFiles'] || []), ...children]
    }
    return []
  }
}
